import React, { useState, useEffect } from 'react';
import {
  connectorDriverService,
  connectorService,
  oidcClientService,
  settingService,
} from '../../services';
import {
  useModalDialog,
  useQueryParams,
  useMessage,
  useSideDialog,
  usePrincipal,
  useGlobalState,
} from '../../hooks';
import {
  Button,
  Checkbox,
  DataTable,
  Input,
  Pill,
  Section,
  Select,
  SelectButtons,
  formatSelectOptions,
  Tabs,
  TextareaAutosize,
} from '../../components';
import { environments, formatData, handleError } from '../../helpers';
import { Datatypes, AttributeSensitivities, AttributeUnits, Statuses } from './';

export const Settings = () => {
  const { queryParams, setHistory, resetParams } = useQueryParams();

  const [settings, setSettings] = useState({});
  const [formData, setFormData] = useState({});
  const [connectorDrivers, setConnectorDrivers] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [oidcClients, setOidcClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOidcClients, setLoadingOidcClients] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });
  const [appSettings] = useGlobalState('settings');

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { fetchPrincipals } = usePrincipal();

  useEffect(() => {
    fetchItems();
    fetchConnectorDrivers();
    fetchConnectors();
    fetchOidcClients();
  }, []);

  const fetchItems = async () => {
    await settingService
      .getAll()
      .then(res => {
        let result = {};
        res.settings.forEach(setting => {
          result[setting.setting_code] = { ...setting };
        });
        setSettings(result);
        setFormData(result);
        setLoading(false);
      })
      .catch(err => {
        showMessage(err, 'error');
        setLoading(false);
      });
  };

  /**
   * Reads the connectors and whether each one is enabled, which the Connectors tab shows beside its
   * name. Enabling a connector is a configuration-file setting, so this is read-only here.
   */
  const fetchConnectors = async () => {
    await connectorService
      .getAll()
      .then(res => setConnectors(res.connectors))
      .catch(err => showMessage(err, 'error'));
  };

  const fetchConnectorDrivers = async () => {
    await connectorDriverService
      .getByConnectorCode('odbc')
      .then(res => {
        setConnectorDrivers(res.connector_drivers);
        setLoading(false);
      })
      .catch(err => {
        showMessage(err, 'error');
        setLoading(false);
      });
  };

  const fetchOidcClients = async () => {
    setLoadingOidcClients(true);
    await oidcClientService
      .getAll()
      .then(res => {
        if (res.oidc_clients) {
          const result = res.oidc_clients.map(x => ({
            ...x,
            oidc_client_secret_expires_timestamp: x.oidc_client_secret_expires_timestamp
              ? formatData(x.oidc_client_secret_expires_timestamp, 'datetime')
              : 'never',
          }));
          setOidcClients(result);
          fetchPrincipals(result, setOidcClients);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        setLoadingOidcClients(false);
      });
  };

  const addConnectorDriver = async data => {
    const connectorDrivers = { connector_drivers: [data] };

    await connectorDriverService
      .create(connectorDrivers)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchConnectorDrivers();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editConnectorDriver = async data => {
    const connectorDrivers = { connector_drivers: Array.isArray(data) ? data : [data] };

    await connectorDriverService
      .update(connectorDrivers)
      .then(async res => {
        fetchConnectorDrivers();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteConnectorDriver = async data => {
    await connectorDriverService
      .destroy({ connector_drivers: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchConnectorDrivers();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const addOidcClient = async data => {
    const oidcClients = { oidc_clients: [data] };

    await oidcClientService
      .create(oidcClients)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        showModalDialog('addedOidcClient', res.oidc_clients);
        fetchOidcClients();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editOidcClient = async data => {
    const oidcClients = { oidc_clients: Array.isArray(data) ? data : [data] };

    await oidcClientService
      .update(oidcClients)
      .then(async res => {
        fetchOidcClients();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteOidcClient = async data => {
    await oidcClientService
      .destroy({ oidc_clients: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchOidcClients();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const generateClientSecret = async data => {
    const oidcClients = { oidc_clients: Array.isArray(data) ? data : [data] };

    await oidcClientService
      .generateSecret({ oidc_clients: data })
      .then(async res => {
        showModalDialog('addedOidcClient', res.oidc_clients);
        clearSelectedItems();
        fetchOidcClients();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editSettings = async data => {
    await settingService
      .update(data)
      .then(async res => {
        fetchItems();
        showMessage(res);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const saveSettings = event => {
    hideModalDialog();
    onSubmit(event);
  };

  const handleInputChange = event => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    setFormData({ ...formData, [name]: { ...formData[name], setting_value: value } });
  };

  const sendTestEmail = async e => {
    e.preventDefault();

    setSendingEmail(true);

    try {
      const config = {
        MAIL_IS_ENABLED: true,
        MAIL_SERVER_TYPE: formData.MAIL_SERVER_TYPE.setting_value,
        MAIL_HOST: formData.MAIL_HOST.setting_value,
        MAIL_PORT: formData.MAIL_PORT.setting_value,
        MAIL_IS_SECURE: formData.MAIL_IS_SECURE.setting_value,
        MAIL_USERNAME: formData.MAIL_USERNAME.setting_value,
        MAIL_PASSWORD: formData.MAIL_PASSWORD.setting_value,
        MAIL_FROM: formData.MAIL_FROM.setting_value,
        FRONTEND_URL:
          (formData?.FRONTEND_URL?.setting_value !== '' && formData?.FRONTEND_URL?.setting_value) ||
          window.location.origin,
      };
      const res = await settingService.sendTestEmail({ config: config });
      showMessage(res);
    } catch (err) {
      showMessage(err, 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);
    let data = formData;

    if (!data?.FRONTEND_URL?.setting_value || data?.FRONTEND_URL?.setting_value === '') {
      data = {
        ...data,
        FRONTEND_URL: {
          ...data.FRONTEND_URL,
          setting_value: window.location.origin,
        },
      };
    }

    let dataToSubmit = [];

    // Remove read-only properties
    Object.entries(data).forEach(([key, value]) => {
      if (!value.setting_is_disabled) {
        dataToSubmit.push({
          setting_id: value.setting_id,
          setting_code: value.setting_code,
          setting_value: value.setting_value,
        });
      }
    });

    // Submit form
    editSettings({ settings: dataToSubmit })
      .catch(err => {
        // Errors handled in editSettings
      })
      .finally(res => {
        setLoading(false);
      });
  };

  const cancel = () => {
    setFormData(settings);
  };

  const rotateOidcSigningKey = async () => {
    await settingService
      .rotateOidcSigningKey({ settings: [settings.REST_API_OIDC_SIGNING_KEY] })
      .then(async res => {
        hideModalDialog();
        showMessage(res);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const onObjectChange = object => {
    resetParams({ object: object });
    //ownedItems.length > 0 && fetchOwnedItems(object)
    setDefaultSelectedObject({ tab: object });
    clearSelectedItems();
  };

  const onTabChange = tab => {
    clearSelectedItems();
    resetParams({ tab: tab, object: undefined });
    //!userGroups && fetchUserGroups()
    //tab === 'owned_assets' && fetchOwnedItems(defaultSelectedObject.tab)
  };

  // What each connector has to configure, keyed by connector code. A connector missing from here has
  // nothing to open onto. `disabled` covers both the page still loading and the connector being
  // switched off: its settings are then shown as they stand, but cannot be changed, since the flag
  // that switches it on lives in the configuration file rather than here.
  const connectorSettings = {
    databricks: disabled => (
      <>
        <h4>Unity Catalog API</h4>
        <div className="form-light-text">
          Katalogue uses the Databricks Unity Catalog API to access the required metadata.
        </div>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.DATABRICKS_BASE_ENDPOINT?.setting_code}
              label={formData?.DATABRICKS_BASE_ENDPOINT?.setting_name}
              value={formData?.DATABRICKS_BASE_ENDPOINT?.setting_value || ''}
              placeholder={formData?.DATABRICKS_BASE_ENDPOINT?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DATABRICKS_BASE_ENDPOINT?.setting_description}
            />

            <Input
              type="text"
              name={formData?.DATABRICKS_CATALOG_ENDPOINT?.setting_code}
              label={formData?.DATABRICKS_CATALOG_ENDPOINT?.setting_name}
              value={formData?.DATABRICKS_CATALOG_ENDPOINT?.setting_value || ''}
              placeholder={formData?.DATABRICKS_CATALOG_ENDPOINT?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DATABRICKS_CATALOG_ENDPOINT?.setting_description}
            />

            <Input
              type="text"
              name={formData?.DATABRICKS_SCHEMA_ENDPOINT?.setting_code}
              label={formData?.DATABRICKS_SCHEMA_ENDPOINT?.setting_name}
              value={formData?.DATABRICKS_SCHEMA_ENDPOINT?.setting_value || ''}
              placeholder={formData?.DATABRICKS_SCHEMA_ENDPOINT?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DATABRICKS_SCHEMA_ENDPOINT?.setting_description}
            />

            <Input
              type="text"
              name={formData?.DATABRICKS_TABLE_ENDPOINT?.setting_code}
              label={formData?.DATABRICKS_TABLE_ENDPOINT?.setting_name}
              value={formData?.DATABRICKS_TABLE_ENDPOINT?.setting_value || ''}
              placeholder={formData?.DATABRICKS_TABLE_ENDPOINT?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DATABRICKS_TABLE_ENDPOINT?.setting_description}
            />
          </div>
        </div>

        <h4>Authentication</h4>
        <div className="form-light-text">
          Only used when a connection authenticates with OAuth M2M (Microsoft Entra Id managed
          service principal), which is Azure only.
        </div>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.DATABRICKS_AZURE_SCOPE?.setting_code}
              label={formData?.DATABRICKS_AZURE_SCOPE?.setting_name}
              value={formData?.DATABRICKS_AZURE_SCOPE?.setting_value || ''}
              placeholder={formData?.DATABRICKS_AZURE_SCOPE?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DATABRICKS_AZURE_SCOPE?.setting_description}
            />
          </div>
        </div>
      </>
    ),
    api: disabled => (
      <>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.API_CONNECTOR_DEFAULT_OPENAPI_FILENAME?.setting_code}
              label={formData?.API_CONNECTOR_DEFAULT_OPENAPI_FILENAME?.setting_name}
              value={formData?.API_CONNECTOR_DEFAULT_OPENAPI_FILENAME?.setting_value || ''}
              placeholder={
                formData?.API_CONNECTOR_DEFAULT_OPENAPI_FILENAME?.setting_default_value || ''
              }
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.API_CONNECTOR_DEFAULT_OPENAPI_FILENAME?.setting_description}
            />
          </div>
        </div>
      </>
    ),
    dbt: disabled => (
      <>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.DBT_DEFAULT_MANIFEST_FILENAME?.setting_code}
              label={formData?.DBT_DEFAULT_MANIFEST_FILENAME?.setting_name}
              value={formData?.DBT_DEFAULT_MANIFEST_FILENAME?.setting_value || ''}
              placeholder={formData?.DBT_DEFAULT_MANIFEST_FILENAME?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.DBT_DEFAULT_MANIFEST_FILENAME?.setting_description}
            />
          </div>
        </div>
      </>
    ),
    ibmdb2: disabled => (
      <>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.IBMDB2_CODEPAGE?.setting_code}
              label={formData?.IBMDB2_CODEPAGE?.setting_name}
              value={formData?.IBMDB2_CODEPAGE?.setting_value || ''}
              placeholder={formData?.IBMDB2_CODEPAGE?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.IBMDB2_CODEPAGE?.setting_description}
            />
          </div>
        </div>
      </>
    ),
    powerbi: disabled => (
      <>
        <h4>Microsoft Fabric API</h4>
        <div className="form-light-text">
          Katalogue reads workspaces, semantic models and semantic model definitions from the Fabric
          API. Change these only to reach a cloud other than the public one.
        </div>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.POWERBI_FABRIC_API_BASE_URL?.setting_code}
              label={formData?.POWERBI_FABRIC_API_BASE_URL?.setting_name}
              value={formData?.POWERBI_FABRIC_API_BASE_URL?.setting_value || ''}
              placeholder={formData?.POWERBI_FABRIC_API_BASE_URL?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.POWERBI_FABRIC_API_BASE_URL?.setting_description}
            />

            <Input
              type="text"
              name={formData?.POWERBI_FABRIC_SCOPE?.setting_code}
              label={formData?.POWERBI_FABRIC_SCOPE?.setting_name}
              value={formData?.POWERBI_FABRIC_SCOPE?.setting_value || ''}
              placeholder={formData?.POWERBI_FABRIC_SCOPE?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.POWERBI_FABRIC_SCOPE?.setting_description}
            />
          </div>
        </div>

        <h4>Power BI API</h4>
        <div className="form-light-text">
          The admin endpoints behind the Scanner API and Hybrid modes are served under the Power BI
          base URL. In Fabric API mode the Power BI API is also used to read Reports. Change these
          only to reach a cloud other than the public one.
        </div>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.POWERBI_API_BASE_URL?.setting_code}
              label={formData?.POWERBI_API_BASE_URL?.setting_name}
              value={formData?.POWERBI_API_BASE_URL?.setting_value || ''}
              placeholder={formData?.POWERBI_API_BASE_URL?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.POWERBI_API_BASE_URL?.setting_description}
            />

            <Input
              type="text"
              name={formData?.POWERBI_SCOPE?.setting_code}
              label={formData?.POWERBI_SCOPE?.setting_name}
              value={formData?.POWERBI_SCOPE?.setting_value || ''}
              placeholder={formData?.POWERBI_SCOPE?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.POWERBI_SCOPE?.setting_description}
            />
          </div>
        </div>

        <h4>Power BI Service</h4>
        <div className="form-light-text">
          This is primarily used to render the "Open in Power BI" links. Change this only to reach a
          cloud other than the public one.
        </div>
        <div className="form-content">
          <div className="form-block vertical">
            <Input
              type="text"
              name={formData?.POWERBI_SERVICE_BASE_URL?.setting_code}
              label={formData?.POWERBI_SERVICE_BASE_URL?.setting_name}
              value={formData?.POWERBI_SERVICE_BASE_URL?.setting_value || ''}
              placeholder={formData?.POWERBI_SERVICE_BASE_URL?.setting_default_value || ''}
              onChange={handleInputChange}
              labelPosition="left"
              disabled={disabled}
              title={formData?.POWERBI_SERVICE_BASE_URL?.setting_description}
            />
          </div>
        </div>
      </>
    ),
    odbc: disabled => (
      <>
        <h4>ODBC Drivers</h4>
        <div className="form-light-text">
          Install and configure the ODBC driver in the OS-specific ODBC datasource manager first,
          then add the driver here to let Katalogue find and use it.
        </div>
        <div className="form-block vertical">
          <DataTable
            columns={[
              { id: 'connector_driver_name', name: 'Driver Name', className: 'bold' },
              { id: 'connector_driver_datasource_type_name', name: 'Datasource Type' },
            ]}
            buttons={
              disabled
                ? undefined
                : [
                    {
                      label: '+ Add',
                      action: 'add',
                      mainButton: true,
                      onClick: items =>
                        showSideDialog('addODBCDriver', items || selectedItems, addConnectorDriver),
                    },
                    {
                      label: 'Edit',
                      action: 'edit',
                      onClick: items =>
                        showSideDialog(
                          'editODBCDriver',
                          items || selectedItems,
                          editConnectorDriver
                        ),
                    },
                    {
                      label: 'Delete',
                      action: 'delete',
                      onClick: items =>
                        showModalDialog(
                          'deleteODBCDriver',
                          items || selectedItems,
                          deleteConnectorDriver
                        ),
                    },
                  ]
            }
            data={connectorDrivers}
            idColumn="connector_driver_id"
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            editable={!disabled}
            loading={loading}
            filterable={true}
            filterObjects={['connector_driver_name', 'connector_driver_datasource_type_name']}
          />
        </div>
      </>
    ),
  };

  // Whether this Katalogue runs its syncs in a worker service of its own rather than in the api
  // service. Only the process that runs them loads connector drivers, so it is the only one that
  // can see a driver fail -- which is why a separate worker's connectors are not reported on here.
  //
  // Only an explicit false says so. An api service that predates this flag does not send it at all,
  // and absence is not evidence: saying nothing is right where the embedded deployment, which is
  // the default, would otherwise be told it has a worker service it does not have.
  const syncsRunInWorkerService = appSettings?.WORKER_IS_ENABLED === false;

  const connectorStatus = {
    enabled: { label: 'Enabled', className: 'pill pill-wide' },
    disabled: {
      label: 'Disabled',
      className: 'pill pill-wide pill-semifull-red',
      tooltip: 'Disabled in the configuration file',
    },
    error: {
      label: 'Error',
      className: 'pill pill-wide pill-full-red',
      tooltip:
        'This connector is enabled but failed to load and cannot be used. See the api service log for the reason.',
    },
  };

  const settingsHasChanged = JSON.stringify(formData) !== JSON.stringify(settings);
  const oidcClientStatus = {
    expired: { label: 'Expired', className: 'pill pill-wide pill-red' },
    disabled: { label: 'Disabled', className: 'pill pill-wide pill-red' },
    enabled: { label: 'Enabled', className: 'pill pill-wide' },
  };

  return (
    <div className="wide-margin">
      <div className="form">
        <form>
          <Tabs
            className="slim left"
            defaultSelectedTab={defaultSelectedTab}
            disableTabsWithoutResults={true}
            orientation="vertical"
            className="large"
            onTabChange={onTabChange}
          >
            <div label="General" tabid="general">
              <div className="form-light-text">Basic settings for this Katalogue instance</div>
              <div className="form-content">
                <h4>Instance Settings</h4>
                <div className="form-block vertical">
                  <Input
                    type="text"
                    name={formData?.FRONTEND_URL?.setting_code}
                    label={formData?.FRONTEND_URL?.setting_name}
                    value={formData?.FRONTEND_URL?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder={window.location.origin}
                    title={formData?.FRONTEND_URL?.setting_description}
                  />
                </div>

                <div className="form-block vertical">
                  <Select
                    value={formData?.ENVIRONMENT?.setting_value || ''}
                    name={formData?.ENVIRONMENT?.setting_code}
                    label={formData?.ENVIRONMENT?.setting_name}
                    options={environments}
                    onChange={handleInputChange}
                    disabled={loading || formData?.ENVIRONMENT?.setting_is_disabled}
                    isNullable={true}
                    isClearable={true}
                    placeholder="Select Environment..."
                    title={formData?.ENVIRONMENT?.setting_description}
                    isOptional={true}
                  />
                </div>
              </div>
            </div>
            <div label="Customization" tabid="customization">
              <div className="form-light-text">
                Settings to customize this Katalogue instance for your organization
              </div>
              <div className="form-content">
                <div className="form-block vertical">
                  <Input
                    type="text"
                    name={formData?.ORGANIZATION_NAME?.setting_code}
                    label={formData?.ORGANIZATION_NAME?.setting_name}
                    value={formData?.ORGANIZATION_NAME?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading || formData?.ORGANIZATION_NAME?.setting_is_disabled}
                    title={formData?.ORGANIZATION_NAME?.setting_description}
                    isOptional={true}
                    placeholder="E.g. Kayenta Consulting AB"
                  />
                </div>
                <div className="form-block vertical">
                  <h4>Support</h4>
                  <div className="form-light-text">
                    Contact details to your organization's Katalogue support
                  </div>

                  <Input
                    type="text"
                    name={formData?.ORGANIZATION_SUPPORT_NAME?.setting_code}
                    label="Team Name"
                    value={formData?.ORGANIZATION_SUPPORT_NAME?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.ORGANIZATION_SUPPORT_NAME?.setting_description}
                    labelPosition="left"
                    placeholder="Support"
                    isOptional={true}
                  />
                  <Input
                    type="text"
                    name={formData?.ORGANIZATION_SUPPORT_URL?.setting_code}
                    label="Email or URL"
                    value={formData?.ORGANIZATION_SUPPORT_URL?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.ORGANIZATION_SUPPORT_URL?.setting_description}
                    labelPosition="left"
                    isOptional={true}
                  />
                </div>
                <div className="form-block vertical">
                  <h4>Internal Resource Link</h4>
                  <div className="form-light-text">
                    Link to your organization's internal Katalogue portal
                  </div>

                  <Input
                    type="text"
                    name={formData?.ORGANIZATION_RESOURCE_NAME?.setting_code}
                    label="Display Name"
                    value={formData?.ORGANIZATION_RESOURCE_NAME?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.ORGANIZATION_RESOURCE_NAME?.setting_description}
                    labelPosition="left"
                    isOptional={true}
                  />

                  <Input
                    type="text"
                    name={formData?.ORGANIZATION_RESOURCE_URL?.setting_code}
                    label="URL"
                    value={formData?.ORGANIZATION_RESOURCE_URL?.setting_value || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.ORGANIZATION_RESOURCE_URL?.setting_description}
                    labelPosition="left"
                    isOptional={true}
                  />
                </div>
              </div>
            </div>

            <div label="Authentication" tabid="authentication">
              <div className="form-light-text">
                Configure authentication methods for users to access Katalogue
              </div>

              <div className="form-content">
                <h4>Local Authentication</h4>
                <div className="form-block vertical">
                  <Checkbox
                    checked={formData?.LOCAL_IS_ENABLED?.setting_value || false}
                    name={formData?.LOCAL_IS_ENABLED?.setting_code}
                    label={formData?.LOCAL_IS_ENABLED?.setting_name}
                    onChange={handleInputChange}
                    disabled={loading || formData?.LOCAL_IS_ENABLED?.setting_is_disabled}
                    title={formData?.LOCAL_IS_ENABLED?.setting_description}
                  />
                </div>
                <h4>Azure OpenID Connect Parameters</h4>
                <div className="form-block vertical">
                  <Checkbox
                    checked={formData?.OIDC_IS_ENABLED?.setting_value || false}
                    name={formData?.OIDC_IS_ENABLED?.setting_code}
                    label={formData?.OIDC_IS_ENABLED?.setting_name}
                    onChange={handleInputChange}
                    disabled={loading || formData?.OIDC_IS_ENABLED?.setting_is_disabled}
                    title={formData?.OIDC_IS_ENABLED?.setting_description}
                  />
                </div>

                {formData?.OIDC_IS_ENABLED?.setting_value && (
                  <div>
                    <div className="form-block vertical">
                      <Input
                        type="text"
                        name={formData?.OIDC_DIRECTORY_ID?.setting_code}
                        label={formData?.OIDC_DIRECTORY_ID?.setting_name}
                        value={formData?.OIDC_DIRECTORY_ID?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading || formData?.OIDC_DIRECTORY_ID?.setting_is_disabled}
                        title={formData?.OIDC_DIRECTORY_ID?.setting_description}
                      />
                    </div>

                    <div className="form-block vertical">
                      <Input
                        type="text"
                        name={formData?.OIDC_APPLICATION_ID?.setting_code}
                        label={formData?.OIDC_APPLICATION_ID?.setting_name}
                        value={formData?.OIDC_APPLICATION_ID?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading || formData?.OIDC_APPLICATION_ID?.setting_is_disabled}
                        title={formData?.OIDC_APPLICATION_ID?.setting_description}
                      />
                    </div>

                    <div className="form-block vertical">
                      <Input
                        type="password"
                        name={formData?.OIDC_CLIENT_SECRET?.setting_code}
                        label={formData?.OIDC_CLIENT_SECRET?.setting_name}
                        value={formData?.OIDC_CLIENT_SECRET?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading || formData?.OIDC_CLIENT_SECRET?.setting_is_disabled}
                        placeholder="< hidden >"
                        autoComplete="new-password"
                        title={formData?.OIDC_CLIENT_SECRET?.setting_description}
                      />
                    </div>

                    <div className="form-block vertical">
                      <Input
                        type="text"
                        name={formData?.OIDC_REDIRECT_URI?.setting_code}
                        label={formData?.OIDC_REDIRECT_URI?.setting_name}
                        value={formData?.OIDC_REDIRECT_URI?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading || formData?.OIDC_REDIRECT_URI?.setting_is_disabled}
                        title={formData?.OIDC_REDIRECT_URI?.setting_description}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div label="User Provisioning" tabid="userProvisioning">
              <div className="form-light-text">
                Configure user provisioning from external directories
              </div>

              <div className="form-content">
                <h4>Microsoft Entra Id (through Microsoft Graph API)</h4>

                {formData?.OIDC_IS_ENABLED?.setting_value ? (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <TextareaAutosize
                        type="text"
                        name={formData?.MSGRAPH_USER_BASE_FILTER?.setting_code}
                        label={formData?.MSGRAPH_USER_BASE_FILTER?.setting_name}
                        value={formData?.MSGRAPH_USER_BASE_FILTER?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        title={formData?.MSGRAPH_USER_BASE_FILTER?.setting_description}
                        isOptional={true}
                        minRows={1}
                      />
                    </div>

                    <div className="form-block vertical">
                      <TextareaAutosize
                        type="text"
                        name={formData?.MSGRAPH_GROUP_BASE_FILTER?.setting_code}
                        label={formData?.MSGRAPH_GROUP_BASE_FILTER?.setting_name}
                        value={formData?.MSGRAPH_GROUP_BASE_FILTER?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        title={formData?.MSGRAPH_GROUP_BASE_FILTER?.setting_description}
                        isOptional={true}
                        minRows={1}
                      />
                    </div>

                    <div className="form-block vertical">
                      <Checkbox
                        checked={formData?.SYNC_NESTED_USER_GROUPS?.setting_value || false}
                        name={formData?.SYNC_NESTED_USER_GROUPS?.setting_code}
                        label={formData?.SYNC_NESTED_USER_GROUPS?.setting_name}
                        onChange={handleInputChange}
                        disabled={loading}
                        title={formData?.SYNC_NESTED_USER_GROUPS?.setting_description}
                      />
                    </div>

                    <div className="form-block vertical">
                      <h4>User Attribute Mapping</h4>
                      <Input
                        type="text"
                        name={formData?.USER_SOURCE_ID?.setting_code}
                        label={formData?.USER_SOURCE_ID?.setting_name}
                        value={formData?.USER_SOURCE_ID?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_SOURCE_ID?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.USER_USERNAME?.setting_code}
                        label={formData?.USER_USERNAME?.setting_name}
                        value={formData?.USER_USERNAME?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_USERNAME?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.USER_FULLNAME?.setting_code}
                        label={formData?.USER_FULLNAME?.setting_name}
                        value={formData?.USER_FULLNAME?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_FULLNAME?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.USER_EMAIL?.setting_code}
                        label={formData?.USER_EMAIL?.setting_name}
                        value={formData?.USER_EMAIL?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_EMAIL?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.USER_TITLE?.setting_code}
                        label={formData?.USER_TITLE?.setting_name}
                        value={formData?.USER_TITLE?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_TITLE?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.USER_DEPARTMENT?.setting_code}
                        label={formData?.USER_DEPARTMENT?.setting_name}
                        value={formData?.USER_DEPARTMENT?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.USER_DEPARTMENT?.setting_description}
                      />
                    </div>

                    <div className="form-block vertical">
                      <h4>User Group Attribute Mapping</h4>
                      <Input
                        type="text"
                        name={formData?.GROUP_SOURCE_ID?.setting_code}
                        label={formData?.GROUP_SOURCE_ID?.setting_name}
                        value={formData?.GROUP_SOURCE_ID?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.GROUP_SOURCE_ID?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.GROUP_NAME?.setting_code}
                        label={formData?.GROUP_NAME?.setting_name}
                        value={formData?.GROUP_NAME?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.GROUP_NAME?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.GROUP_EMAIL?.setting_code}
                        label={formData?.GROUP_EMAIL?.setting_name}
                        value={formData?.GROUP_EMAIL?.setting_value || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.GROUP_EMAIL?.setting_description}
                      />

                      <Input
                        type="text"
                        name={formData?.GROUP_DESCRIPTION?.setting_code}
                        label={formData?.GROUP_DESCRIPTION?.setting_name || ''}
                        value={formData?.GROUP_DESCRIPTION?.setting_value}
                        onChange={handleInputChange}
                        disabled={loading}
                        labelPosition="left"
                        title={formData?.GROUP_DESCRIPTION?.setting_description}
                      />
                    </div>
                  </React.Fragment>
                ) : (
                  <div className="form-block vertical">
                    To use this feature, authentication through Azure OpenID Connect must be enabled
                    and correctly configured.
                  </div>
                )}
              </div>
            </div>

            <div label="Notifications" tabid="notifications">
              <div className="form-light-text">
                Configure methods for this Katalogue instance to notify users
              </div>

              <div className="form-content">
                <div className="form-block vertical">
                  <Checkbox
                    checked={formData?.MAIL_IS_ENABLED?.setting_value || false}
                    name={formData?.MAIL_IS_ENABLED?.setting_code}
                    label={formData?.MAIL_IS_ENABLED?.setting_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.MAIL_IS_ENABLED?.setting_description}
                  />
                </div>

                {formData?.MAIL_IS_ENABLED?.setting_value && (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <h4>Email Server</h4>
                      <SelectButtons
                        name={formData?.MAIL_SERVER_TYPE?.setting_code}
                        label={formData?.MAIL_SERVER_TYPE?.setting_name}
                        value={formData?.MAIL_SERVER_TYPE?.setting_value || 0}
                        options={formatSelectOptions({
                          options: [
                            { label: 'Azure', value: 'msgraph' },
                            { label: 'SMTP', value: 'smtp' },
                          ],
                          optionValue: 'value',
                          optionLabel: 'label',
                        })}
                        onChange={handleInputChange}
                        disabled={loading || formData?.MAIL_SERVER_TYPE?.setting_is_disabled}
                        title={formData?.MAIL_SERVER_TYPE?.setting_description}
                      />
                    </div>

                    {formData?.MAIL_SERVER_TYPE?.setting_value === 'smtp' && (
                      <React.Fragment>
                        <div className="form-block vertical">
                          <Input
                            type="text"
                            name={formData?.MAIL_HOST?.setting_code}
                            label={formData?.MAIL_HOST?.setting_name}
                            value={formData?.MAIL_HOST?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_HOST?.setting_is_disabled}
                            title={formData?.MAIL_HOST?.setting_description}
                          />
                          <Input
                            type="text"
                            name={formData?.MAIL_PORT?.setting_code}
                            label={formData?.MAIL_PORT?.setting_name}
                            value={formData?.MAIL_PORT?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_PORT?.setting_is_disabled}
                            placeholder={formData?.MAIL_IS_SECURE?.setting_value ? 465 : 587}
                            title={formData?.MAIL_PORT?.setting_description}
                          />
                          <Input
                            type="text"
                            name={formData?.MAIL_USERNAME?.setting_code}
                            label={formData?.MAIL_USERNAME?.setting_name}
                            value={formData?.MAIL_USERNAME?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_USERNAME?.setting_is_disabled}
                            autoComplete="off"
                            title={formData?.MAIL_USERNAME?.setting_description}
                          />
                          <Input
                            type="password"
                            name={formData?.MAIL_PASSWORD?.setting_code}
                            label={formData?.MAIL_PASSWORD?.setting_name}
                            value={formData?.MAIL_PASSWORD?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_PASSWORD?.setting_is_disabled}
                            placeholder="< hidden >"
                            autoComplete="new-password"
                            title={formData?.MAIL_PASSWORD?.setting_description}
                          />
                          <Input
                            type="text"
                            name={formData?.MAIL_FROM?.setting_code}
                            label={formData?.MAIL_FROM?.setting_name}
                            value={formData?.MAIL_FROM?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_FROM?.setting_is_disabled}
                            title={formData?.MAIL_FROM?.setting_description}
                          />
                        </div>
                        <div className="form-block vertical">
                          <Checkbox
                            checked={formData?.MAIL_IS_SECURE?.setting_value || false}
                            name={formData?.MAIL_IS_SECURE?.setting_code}
                            label={formData?.MAIL_IS_SECURE?.setting_name}
                            onChange={handleInputChange}
                            disabled={loading || formData?.MAIL_IS_SECURE?.setting_is_disabled}
                            title={formData?.MAIL_IS_SECURE?.setting_description}
                          />
                        </div>
                      </React.Fragment>
                    )}

                    {formData?.MAIL_SERVER_TYPE?.setting_value === 'msgraph' && (
                      <div className="form-block vertical">
                        {formData?.OIDC_IS_ENABLED?.setting_value ? (
                          <Input
                            type="text"
                            name={formData?.MAIL_FROM?.setting_code}
                            label={formData?.MAIL_FROM?.setting_name}
                            value={formData?.MAIL_FROM?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading}
                            labelPosition={
                              formData?.MAIL_SERVER_TYPE?.setting_value === 'smtp'
                                ? 'left'
                                : undefined
                            }
                            title={formData?.MAIL_FROM?.setting_description}
                          />
                        ) : (
                          <div className="form-block vertical">
                            To send emails through Azure (Microsoft Graph API), authentication
                            through Azure OpenID Connect must be enabled and correctly configured.
                          </div>
                        )}
                      </div>
                    )}
                    {((formData?.MAIL_SERVER_TYPE?.setting_value === 'msgraph' &&
                      formData?.OIDC_IS_ENABLED?.setting_value) ||
                      formData?.MAIL_SERVER_TYPE?.setting_value === 'smtp') && (
                      <React.Fragment>
                        <div className="form-block vertical">
                          <Button
                            className={'button' + (sendingEmail ? ' loading' : '')}
                            value={sendingEmail ? 'SENDING...' : 'SEND TEST EMAIL'}
                            tooltip={
                              (sendingEmail ? 'Sending' : 'Send') +
                              ' a test email to your email address'
                            }
                            disabled={loading || sendingEmail}
                            onClick={sendTestEmail}
                          />
                        </div>

                        <div className="form-block vertical">
                          <h4>Notification Settings</h4>
                          <Input
                            type="text"
                            name={formData?.MAIL_SYSTEM_EMAILS_TO?.setting_code}
                            label={formData?.MAIL_SYSTEM_EMAILS_TO?.setting_name}
                            value={formData?.MAIL_SYSTEM_EMAILS_TO?.setting_value || ''}
                            onChange={handleInputChange}
                            disabled={loading}
                            title={formData?.MAIL_SYSTEM_EMAILS_TO?.setting_description}
                            isOptional={true}
                          />
                        </div>
                        <div className="form-block vertical">
                          <Checkbox
                            checked={formData?.MAIL_SEND_WELCOME_EMAILS?.setting_value || false}
                            name={formData?.MAIL_SEND_WELCOME_EMAILS?.setting_code}
                            label={formData?.MAIL_SEND_WELCOME_EMAILS?.setting_name}
                            onChange={handleInputChange}
                            disabled={loading}
                            title={formData?.MAIL_SEND_WELCOME_EMAILS?.setting_description}
                          />
                        </div>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>

            <div label="Password Managers" tabid="passwordManagers">
              <div className="form-light-text">
                Configure integration with external password managers to let Connections fetch
                passwords from them.
              </div>

              <div className="form-content">
                <h4>Azure Key Vault</h4>
                {formData?.OIDC_IS_ENABLED?.setting_value ? (
                  <React.Fragment>
                    <div className="form-block vertical">
                      <Checkbox
                        checked={formData?.AZURE_KEY_VAULT_IS_ENABLED?.setting_value || false}
                        name={formData?.AZURE_KEY_VAULT_IS_ENABLED?.setting_code}
                        label={formData?.AZURE_KEY_VAULT_IS_ENABLED?.setting_name}
                        onChange={handleInputChange}
                        disabled={loading}
                        title={formData?.AZURE_KEY_VAULT_IS_ENABLED?.setting_description}
                      />
                    </div>

                    {formData?.AZURE_KEY_VAULT_IS_ENABLED?.setting_value && (
                      <div className="form-block vertical">
                        <Input
                          type="text"
                          name={formData?.AZURE_KEY_VAULT_NAME?.setting_code}
                          label={formData?.AZURE_KEY_VAULT_NAME?.setting_name}
                          value={formData?.AZURE_KEY_VAULT_NAME?.setting_value || ''}
                          onChange={handleInputChange}
                          disabled={loading}
                          placeholder="<key-vault-name>.vault.azure.net"
                          title={formData?.AZURE_KEY_VAULT_NAME?.setting_description}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ) : (
                  <div className="form-block vertical">
                    To use this feature, authentication through Azure OpenID Connect must be enabled
                    and correctly configured.
                  </div>
                )}
              </div>
            </div>

            <div label="REST API" tabid="rest_api">
              <div className="form-light-text">Configure the Katalogue REST API</div>

              <div className="form-content">
                <div className="form-block vertical">
                  <Checkbox
                    checked={formData?.REST_API_IS_ENABLED?.setting_value || false}
                    name={formData?.REST_API_IS_ENABLED?.setting_code}
                    label={formData?.REST_API_IS_ENABLED?.setting_name}
                    onChange={handleInputChange}
                    disabled={loading || formData?.REST_API_IS_ENABLED?.setting_is_disabled}
                    title={formData?.REST_API_IS_ENABLED?.setting_description}
                  />
                </div>

                {formData?.REST_API_IS_ENABLED?.setting_value && (
                  <div className="form-block vertical">
                    <Button
                      value="ROTATE SIGNING KEY"
                      onClick={() =>
                        showModalDialog('rotateOidcSigningKey', undefined, rotateOidcSigningKey)
                      }
                      tooltip={
                        `Rotate private key used to sign the REST API access tokens.\nAll issued access tokens will instantly be invalidated when the signing key is rotated.` +
                        (settings?.REST_API_OIDC_SIGNING_KEY?.setting_is_disabled
                          ? `\nThis action is disabled since an external signing key is used.`
                          : '')
                      }
                      disabled={settings?.REST_API_OIDC_SIGNING_KEY?.setting_is_disabled}
                    />
                  </div>
                )}
              </div>

              {formData?.REST_API_IS_ENABLED?.setting_value && (
                <React.Fragment>
                  <h4>Clients</h4>
                  <DataTable
                    columns={[
                      {
                        id: 'oidc_client_name',
                        name: 'Name',
                        className: 'bold',
                        tooltip: 'oidc_client_description',
                      },
                      { id: 'oidc_client_code', name: 'Client Id', className: 'small-text' },
                      {
                        id: 'oidc_client_description',
                        name: 'Description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'oidc_client_secret_expires_timestamp',
                        name: 'Secret Expires At',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'oidc_client_last_login_timestamp',
                        name: 'Last Login',
                        type: 'datetime',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'oidc_client_status',
                        name: 'Status',
                        type: 'pill',
                        value: 'oidc_client_status',
                        values: oidcClientStatus,
                      },
                      {
                        id: 'principal_name',
                        name: 'Owner',
                        type: 'owner',
                        link: '/:principal_url',
                        data: { photo: 'principal_photo' },
                        tooltipSubtitle: 'principal_type_name',
                      },
                    ]}
                    buttons={[
                      {
                        label: '+ Add',
                        action: 'add',
                        mainButton: true,
                        onClick: items =>
                          showSideDialog('addOidcClient', items || selectedItems, addOidcClient),
                      },
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editOidcClient', items || selectedItems, editOidcClient),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteOidcClient',
                            items || selectedItems,
                            deleteOidcClient
                          ),
                      },
                      {
                        label: 'Generate New Secret',
                        action: 'generate_client_secret',
                        onClick: items =>
                          showModalDialog(
                            'generateClientSecret',
                            items || selectedItems,
                            generateClientSecret
                          ),
                      },
                    ]}
                    data={oidcClients}
                    idColumn="oidc_client_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    editable={true}
                    loading={loadingOidcClients}
                    filterable={true}
                    filterObjects={[
                      'oidc_client_name',
                      'oidc_client_code',
                      'oidc_client_description',
                      'oidc_client_status',
                      'principal_name',
                    ]}
                    filterObjectsValueMap={{ oidc_client_status: oidcClientStatus }}
                    showViewModes={true}
                  />

                  <h4>REST API Endpoints</h4>
                  <div className="">
                    <div className="horizontal-label">
                      <div className="label">Token Endpoint</div>
                      <div className="text">{appSettings?.REST_API_TOKEN_URL}</div>
                    </div>

                    <div className="horizontal-label">
                      <div className="label">API Docs Endpoint</div>
                      <div className="text">
                        <a
                          className="link external-link"
                          href={appSettings?.REST_API_DOCS_URL}
                          target="_blank"
                        >
                          {appSettings?.REST_API_DOCS_URL}
                        </a>
                      </div>
                    </div>

                    <div className="horizontal-label">
                      <div className="label">Resource Endpoints</div>
                      <div className="text">{appSettings?.REST_API_RESOURCE_ENDPOINTS_URL}</div>
                    </div>
                  </div>
                </React.Fragment>
              )}
            </div>

            <div label="Connectors" tabid="connectors">
              <div className="form-light-text">
                Connector settings. See the docs on how to enable and disable connectors.
                {syncsRunInWorkerService && (
                  <>
                    {`\n`}
                    Syncs run in a separate worker service, which is where connector drivers are
                    loaded — a connector that fails to load there is reported in the worker service
                    log, not here.
                  </>
                )}
              </div>
              <div className="form-block-wide">
                {connectors.map(connector => {
                  const settings = connectorSettings[connector.connector_code];
                  // Only a disabled connector is read-only: the installation has switched it off,
                  // so there is nothing to configure it for. A connector that failed to load stays
                  // editable, because its settings are a likely cause of the failure -- a wrong
                  // Oracle Instant Client path, say -- and fixing one is how it is recovered.
                  const isDisabled = connector.connector_status === 'disabled';

                  return (
                    <Section
                      key={connector.connector_code}
                      icon={formatData(connector.connector_code, 'icon')}
                      heading={connector.connector_name}
                      muted={isDisabled}
                      status={<Pill label={connector.connector_status} values={connectorStatus} />}
                      expandable={!!settings}
                    >
                      {settings && settings(loading || isDisabled)}
                    </Section>
                  );
                })}
              </div>
            </div>

            <div label="Logging" tabid="logging">
              <div className="form-light-text">Configure logging for this Katalogue instance</div>

              <div className="form-content">
                <div className="form-block vertical">
                  <SelectButtons
                    name={formData?.LOG_LEVEL?.setting_code}
                    label={formData?.LOG_LEVEL?.setting_name}
                    value={formData?.LOG_LEVEL?.setting_value || 0}
                    options={formatSelectOptions({
                      options: [
                        { label: 'Info', value: 'info' },
                        { label: 'Debug', value: 'debug' },
                      ],
                      optionValue: 'value',
                      optionLabel: 'label',
                    })}
                    onChange={handleInputChange}
                    disabled={loading || formData?.LOG_LEVEL?.setting_is_disabled}
                    title={formData?.LOG_LEVEL?.setting_description}
                  />
                </div>

                <div className="form-block vertical">
                  <Checkbox
                    checked={formData?.HTTP_LOGGING_IS_ENABLED?.setting_value || false}
                    name={formData?.HTTP_LOGGING_IS_ENABLED?.setting_code}
                    label={formData?.HTTP_LOGGING_IS_ENABLED?.setting_name}
                    onChange={handleInputChange}
                    disabled={loading}
                    title={formData?.HTTP_LOGGING_IS_ENABLED?.setting_description}
                  />
                </div>
              </div>
            </div>

            <div label="Enumerations" tabid="enumerations">
              <Tabs
                className="slim left"
                defaultSelectedTab={defaultSelectedObject}
                disableTabsWithoutResults={true}
                onTabChange={onObjectChange}
              >
                <div label="Asset Status" tabid="asset_status">
                  <Statuses />
                </div>

                <div label="Datatypes" tabid="datatypes">
                  <Datatypes />
                </div>

                <div label="Attribute Sensitivity" tabid="attribute_sensitivity">
                  <AttributeSensitivities />
                </div>

                <div label="Attribute Units" tabid="attribute_units">
                  <AttributeUnits />
                </div>
              </Tabs>
            </div>
          </Tabs>

          {formData && (
            <div className="main-toolbar">
              <Button
                buttonStyle="main-button"
                value={loading ? 'Saving...' : 'Save'}
                className={loading ? ' loading' : ''}
                disabled={loading || !settingsHasChanged}
                onClick={event => {
                  event.preventDefault();
                  showModalDialog('editSettings', undefined, () => saveSettings(event));
                }}
              />
              <Button value="Cancel" onClick={cancel} disabled={loading || !settingsHasChanged} />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
